import {Injectable} from '@angular/core';
import {AnnouncementCategory} from '@app/models/enums/announcement-category';
import {GtManageOrganizationService} from '@app/services/gt-manage-organization.service';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {DownloadLocation, MediaFileStoreModel, MediaFileStoreService} from '@app/services/media-file-store.service';
import {checkResponse} from '@app/shared/utils/app-utils';
import {MediaAdministrationService, MediaFile} from '@gt-api';
import {BehaviorSubject, EMPTY, from, Observable, of} from 'rxjs';
import {catchError, map, mapTo, switchMap, tap} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class MediaFileControllerService {
  private audio = new Audio();
  private playingState = new BehaviorSubject('');

  playing$ = this.playingState.asObservable();

  constructor(
    private mediaFileStoreService: MediaFileStoreService,
    private mediaAdministrationService: MediaAdministrationService,
    private manageOrganizationService: GtManageOrganizationService,
    private notificationService: GtNotificationService
  ) {
    this.observePlayable();
  }

  custom$ = this.mediaFileStoreService.mediaFiles$.pipe(
    map(mediaFiles => mediaFiles.filter(mf => mf.category === AnnouncementCategory.Custom))
  );

  global$ = this.mediaFileStoreService.mediaFiles$.pipe(
    map(mediaFiles => mediaFiles.filter(mf => mf.category !== AnnouncementCategory.Custom))
  );

  byCategory$(category: AnnouncementCategory): Observable<MediaFileStoreModel[]> {
    return this.mediaFileStoreService.mediaFiles$.pipe(
      map(mediaFiles => mediaFiles.filter(mf => mf.category === category || mf.category === AnnouncementCategory.Custom))
    );
  }

  byFileName$(fileName: string): Observable<MediaFileStoreModel> {
    return this.mediaFileStoreService.mediaFiles$.pipe(map(mediaFiles => mediaFiles.find(mf => mf.fileName === fileName)));
  }

  reload() {
    this.mediaFileStoreService.reload();
  }

  play(fileName: string) {
    this.mediaFileStoreService.updatePlayableLocation(null);
    this.getDownloadLocation(fileName)
      .pipe(
        map(
          downloadPath =>
            ({
              fileName,
              downloadPath
            } as DownloadLocation)
        ),
        tap(downloadLocation => this.mediaFileStoreService.updatePlayableLocation(downloadLocation)),
        catchError(error => {
          this.notificationService.customError(error);
          return of();
        })
      )
      .subscribe();
  }

  stop() {
    this.mediaFileStoreService.updatePlayableLocation(null);
  }

  update(mediaFile: MediaFileStoreModel) {
    return this.mediaAdministrationService
      .editMediaMetadata1(this.manageOrganizationService.manageOname, this.manageOrganizationService.manageLogin, {
        fileName: mediaFile.id,
        displayName: mediaFile.displayName,
        description: mediaFile.description
      } as MediaFile)
      .pipe(
        checkResponse(),
        tap(_ => this.mediaFileStoreService.reload()),
        mapTo(void 0)
      );
  }

  delete(fileName: string) {
    return this.mediaAdministrationService
      .deleteMediaFile1(this.manageOrganizationService.manageOname, this.manageOrganizationService.manageLogin, fileName)
      .pipe(
        tap(response => {
          if (response.code === 409) {
            throw new Error('announcement.already_in_use');
          } else if (response.code === 403) {
            throw new Error('announcement.forbidden');
          } else if (response.code !== 200) {
            console.log('Unable to delete announcement', response);
            throw new Error(response.message);
          }
          this.mediaFileStoreService.reload();
        }),
        mapTo(void 0)
      );
  }

  getDownloadLocation(fileName: string) {
    return this.mediaAdministrationService
      .downloadMediaFile1(this.manageOrganizationService.manageOname, this.manageOrganizationService.manageLogin, fileName)
      .pipe(
        map(response => {
          if (response.code === 200) {
            return response.downloadPath;
          } else if (response.code === 404) {
            throw new Error('announcement.error_file_not_found');
          } else {
            throw new Error(response.message);
          }
        })
      );
  }

  private observePlayable() {
    this.audio.onended = _ => this.playingState.next(null);
    this.mediaFileStoreService.downloadLocation$
      .pipe(
        switchMap(downloaded => {
          this.audio.pause();
          if (!downloaded || !downloaded.downloadPath) {
            this.playingState.next('');
            return EMPTY;
          }
          this.audio.src = downloaded.downloadPath;
          this.audio.load();
          return from(this.audio.play() || Promise.resolve()).pipe(tap(() => this.playingState.next(downloaded.fileName)));
        })
      )
      .subscribe();
  }
}
